import {
  Calendar,
  GenericStatus,
  GenericStatusColorType,
  GenericStatusLabel,
  TableColumn,
  Tooltip} from '@hevoio/hevo-ui-toolkit';
import styles from './ConnectivityColumns.module.scss';
import { VPCPeeringConnection, VPCPeeringStatus } from '../interface';


const STATUS_DATA_MAP: { [key: string]: GenericStatus<string> } = {
  ACTIVE: {
    value: 'ACTIVE',
    displayName: 'Available',
    icon: 'circle-tick',
    colorType: GenericStatusColorType.SUCCESS
  },
  PENDING: {
    value: 'PENDING',
    displayName: 'Pending',
    icon: 'error-outline',
    colorType: GenericStatusColorType.WARNING
  },
  PENDING_ACCEPTANCE: {
    value: 'PENDING_ACCEPTANCE',
    displayName: 'Pending Acceptance',
    icon: 'error-outline',
    colorType: GenericStatusColorType.INFO
  },
  REQUEST_SUBMITTED: {
    value: 'REQUEST_SUBMITTED',
    displayName: 'Request Submitted',
    icon: 'error-outline',
    colorType: GenericStatusColorType.WARNING
  },
  FAILED: {
    value: 'FAILED',
    displayName: 'Failed',
    icon: 'error-outline',
    colorType: GenericStatusColorType.ERROR
  },
  DELETED: {
    value: 'DELETED',
    displayName: 'Deleted',
    icon: 'trash',
    colorType: GenericStatusColorType.DISABLED
  }
};

const getStatus = (status?: VPCPeeringStatus): GenericStatus<string> =>
  STATUS_DATA_MAP[status?.value ?? ''] ?? {
    value: status?.value ?? '',
    displayName: status?.name ?? status?.value ?? '',
    colorType: GenericStatusColorType.DEFAULT
  };

export const getConnectivityColumns = (): TableColumn<VPCPeeringConnection>[] => [
  {
    id: 'name',
    field: 'name',
    headerName: 'Connection Name',
    width: 220,
    // Flexible columns ignore `width`; without minWidth the column collapses
    // to 0 when the fixed columns overflow the container (toolkit >= 1.21).
    minWidth: 220,
    isFlexible: true
  },
  {
    id: 'created_at',
    field: 'created_at',
    headerName: 'Created At',
    width: 160,
    renderCell: ({ row }) => <Calendar value={row.data.created_at} />
  },
  {
    id: 'hevo_cidr',
    field: 'hevo_cidr',
    headerName: 'Hevo CIDR',
    width: 160,
    valueGetter: ({ row }) => row.data.hevo_cidr ?? ''
  },
  {
    id: 'peering_id',
    field: 'peering_id',
    headerName: 'Peering ID',
    width: 160,
    valueGetter: ({ row }) => row.data.peering_id ?? ''
  },
  {
    id: 'cidr',
    field: 'cidr',
    headerName: 'VPC CIDR (Region)',
    width: 260,
    valueGetter: ({ row }) => {
      const cidrs = row.data.cidr?.join(', ') ?? '';
      return row.data.aws_region ? `${cidrs} (${row.data.aws_region})` : cidrs;
    }
  },
  { id: 'vpc_id', field: 'vpc_id', headerName: 'VPC ID', width: 200 },
  { id: 'aws_account_id', field: 'aws_account_id', headerName: 'AWS Account ID', width: 180 },
  {
    id: 'status',
    field: 'status',
    headerName: 'Status',
    width: 200,
    headerCellAlign: 'flex-end',
    withoutBorder: true,
    cellAlign: 'flex-end',
    cellClassName: styles.stickyStatusCell,
    headerClassName: styles.stickyStatusHeader,
    renderCell: ({ row }) => {
      const helperText = row.data.message ?? row.data.status?.description;
      return (
        <Tooltip
          dataId={`status-tooltip-${row.data.id}`}
          title={helperText ?? ''}
          disabled={!helperText}
        >
          <span>
            <GenericStatusLabel status={getStatus(row.data.status)} withIcon />
          </span>
        </Tooltip>
      );
    }
  }
];
